// 権利確定日そのものはクロール対象外（月単位のrecordMonthsしか持たない）ため、
// 各権利確定月の末日を権利確定日とみなして近似する（実際の営業日ベースの
// 最終営業日とずれる場合がある、という前提。docs/design.mdに記載）。
export interface NextRecordDate {
  date: Date
  daysUntil: number
}

function lastDayOfMonth(year: number, month: number): Date {
  return new Date(year, month, 0)
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

// 土日のみを非営業日として数える近似（祝日は考慮しない）。日本株の受渡は
// T+2のため、権利確定日の2営業日前（権利付き最終日）までに買付が必要。
function subtractBusinessDays(date: Date, count: number): Date {
  const result = new Date(date)
  let remaining = count
  while (remaining > 0) {
    result.setDate(result.getDate() - 1)
    const day = result.getDay()
    if (day !== 0 && day !== 6) remaining--
  }
  return result
}

export function nextRecordDate(
  recordMonths: number[],
  now: Date = new Date()
): NextRecordDate | null {
  if (recordMonths.length === 0) return null

  const today = startOfDay(now)
  const year = today.getFullYear()

  const candidates = recordMonths.flatMap((month) => [
    lastDayOfMonth(year, month),
    lastDayOfMonth(year + 1, month),
  ])

  const upcoming = candidates.filter((d) => d >= today).sort((a, b) => a.getTime() - b.getTime())[0]

  if (!upcoming) return null

  const daysUntil = Math.round((upcoming.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
  return { date: upcoming, daysUntil }
}

// カード右上のリボン表示用: 今月がrecordMonthsに含まれ、かつ買付期限
// （権利付き最終日、権利確定日の2営業日前の概算）がまだ過ぎていない場合のみ、
// その買付期限までの残り日数を返す（一覧全体の並び替えに使うnextRecordDateとは
// 異なり、他の月の権利確定日は対象外）。期限を過ぎた月は「今買っても今回の優待
// には間に合わない」ため表示しない。
export interface RecordBuyDeadline {
  recordDate: Date
  buyDeadline: Date
  daysUntilBuyDeadline: number
}

export function recordDeadlineThisMonth(
  recordMonths: number[],
  now: Date = new Date()
): RecordBuyDeadline | null {
  const currentMonth = now.getMonth() + 1
  if (!recordMonths.includes(currentMonth)) return null

  const today = startOfDay(now)
  const recordDate = lastDayOfMonth(today.getFullYear(), currentMonth)
  const buyDeadline = subtractBusinessDays(recordDate, 2)
  if (buyDeadline < today) return null

  const daysUntilBuyDeadline = Math.round(
    (buyDeadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  )
  return { recordDate, buyDeadline, daysUntilBuyDeadline }
}

export function formatRecordBuyDeadline({
  buyDeadline,
  daysUntilBuyDeadline,
}: RecordBuyDeadline): string {
  const label = `${buyDeadline.getMonth() + 1}/${buyDeadline.getDate()}`
  if (daysUntilBuyDeadline === 0) return `${label} 権利獲得 (本日)`
  return `${label} 権利獲得 (あと${daysUntilBuyDeadline}日)`
}
